import { Link } from "react-router-dom";
import { ScrollText, ArrowLeft } from "lucide-react";

/**
 * Public Terms & Conditions page. Required by Twilio toll-free verification
 * (the "Terms & Conditions URL" field on Step 3/3).
 */
export default function TermsPage() {
  return (
    <div className="min-h-screen bg-stone-50">
      <header className="bg-[#0B1220] text-white py-10 px-6">
        <div className="max-w-3xl mx-auto">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-xs text-stone-300 hover:text-white mb-4"
          >
            <ArrowLeft size={12} /> Back to app
          </Link>
          <div className="canales-overline text-stone-300 flex items-center gap-2">
            <ScrollText size={12} /> Legal · Terms &amp; Conditions
          </div>
          <h1 className="font-display text-3xl md:text-5xl mt-2 leading-[1.05]">
            Terms &amp; Conditions
          </h1>
          <p className="text-stone-300 mt-3 max-w-2xl text-sm md:text-base">
            Rules governing the use of the Canales Franchise LLC internal
            operational audit platform, including the SMS notification program.
          </p>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10 space-y-6">
        <P><strong>Effective date:</strong>{" "}{new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</P>

        <H>1. Acceptance</H>
        <P>
          By using the audit platform (web, mobile, or PDF reports) operated by{" "}
          <strong>Canales Franchise LLC</strong> (the “Company”), you agree to
          these Terms &amp; Conditions and to our{" "}
          <Link to="/privacy" className="underline font-medium">Privacy Policy</Link>.
        </P>

        <H>2. Eligibility</H>
        <P>
          The platform is intended exclusively for authorized employees and
          franchise partners of the Company. Access is granted by the Company
          administrator on a case-by-case basis. The platform is not directed at
          consumers or the general public.
        </P>

        <H>3. Accounts and security</H>
        <ul className="list-disc pl-6 space-y-1 text-[14.5px] text-stone-700">
          <li>You are responsible for keeping your login credentials confidential.</li>
          <li>Any activity performed under your account is your responsibility.</li>
          <li>Notify the administrator immediately of any suspected unauthorized access.</li>
        </ul>

        <H>4. SMS notification program</H>
        <P>
          The platform sends operational SMS notifications (audit findings, task
          assignments, follow-up reminders) <strong>only</strong> to phone
          numbers captured by the Company administrator and only for the
          notification categories explicitly enabled in the user profile. By
          having your phone number captured in the platform, you acknowledge
          that you have authorized the Company to contact you on that number
          for those operational purposes.
        </P>
        <P>
          Full details, including how to revoke consent (reply <strong>STOP</strong>)
          and request help (reply <strong>HELP</strong>), are described on our{" "}
          <Link to="/sms-terms" className="underline font-medium">
            SMS Terms &amp; Opt-In page
          </Link>
          . Standard message and data rates may apply.
        </P>

        <H>5. Acceptable use</H>
        <P>
          You agree <strong>not</strong> to: (a) use the platform for any
          unlawful purpose; (b) attempt to circumvent security controls or
          access data you are not authorized to see; (c) upload malicious code,
          fraudulent photos, or fabricated audit responses; (d) share your
          login with unauthorized people; (e) use the platform to harass,
          defame, or discriminate against any person.
        </P>

        <H>6. Intellectual property</H>
        <P>
          All content, branding, software, checklists, audit templates, and
          documents available through the platform are the property of Canales
          Franchise LLC or its licensors. You may use them only in connection
          with your role.
        </P>

        <H>7. Data and confidentiality</H>
        <P>
          Audit data — including photos, store scores, findings, and reports —
          is confidential business information of the Company. You may not
          disclose it to anyone outside the Company without written
          authorization.
        </P>

        <H>8. Disclaimer</H>
        <P>
          The platform is provided “as is” and “as available”, without
          warranties of any kind, express or implied, including but not limited
          to fitness for a particular purpose, accuracy of the AI-generated
          summaries, or uninterrupted availability.
        </P>

        <H>9. Limitation of liability</H>
        <P>
          To the maximum extent permitted by law, Canales Franchise LLC shall
          not be liable for any indirect, incidental, special, consequential, or
          punitive damages arising from or related to your use of the platform.
        </P>

        <H>10. Termination</H>
        <P>
          The Company may suspend or terminate your access at any time, with or
          without notice, for any reason, including for violations of these
          Terms.
        </P>

        <H>11. Governing law</H>
        <P>
          These Terms are governed by the laws of the State of Texas, United
          States, without regard to its conflict-of-law principles.
        </P>

        <H>12. Contact</H>
        <P>
          Questions about these Terms? Write to{" "}
          <a href="mailto:antonio@canalesfranchise.com" className="underline">
            antonio@canalesfranchise.com
          </a>
          .
        </P>

        <div className="text-xs text-stone-500 pt-6 border-t border-stone-200 mt-8">
          © {new Date().getFullYear()} Canales Franchise LLC · All rights reserved.
        </div>
      </main>
    </div>
  );
}

function H({ children }) { return <h2 className="font-display text-2xl mt-6 mb-2 text-stone-900">{children}</h2>; }
function P({ children }) { return <p className="text-[14.5px] text-stone-700 leading-relaxed">{children}</p>; }
